import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import Skeleton from "react-loading-skeleton";
import { QRCodeSVG } from "qrcode.react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import CustomError from "../../../../utils/customError";
import { handleServerValidations } from "../../../../utils/commonfunction";
import { LOADER_TIMEOUT_TIME, NOTION_DOCS, SWAL_SETTINGS } from "../../../../utils/Constants";
import {
  getLineIntegrationSettings,
  updateLineIntegrationSettings,
} from "../../services/restaurant.services";

interface LineIntegrationSettings {
  line_enabled: boolean;
  line_login_channel_id: string;
  line_oa_basic_id: string;
  has_channel_secret: boolean;
  line_login_channel_secret_masked: string | null;
  has_access_token: boolean;
  line_channel_access_token_masked: string | null;
  add_friend_url: string | null;
  add_friend_qr_value: string | null;
  callback_url: string;
}

interface LineIntegrationFormValues {
  line_enabled: boolean;
  line_login_channel_id: string;
  line_login_channel_secret: string;
  line_oa_basic_id: string;
  line_channel_access_token: string;
}

interface UpdateLineIntegrationPayload {
  o_id: string;
  line_enabled: boolean;
  line_login_channel_id: string;
  line_oa_basic_id: string;
  line_login_channel_secret?: string;
  line_channel_access_token?: string;
}

interface ServiceResponse<T> {
  success?: boolean;
  message?: string;
  data?: T;
}

const INITIAL_SETTINGS: LineIntegrationSettings = {
  line_enabled: false,
  line_login_channel_id: "",
  line_oa_basic_id: "",
  has_channel_secret: false,
  line_login_channel_secret_masked: null,
  has_access_token: false,
  line_channel_access_token_masked: null,
  add_friend_url: null,
  add_friend_qr_value: null,
  callback_url: "",
};

const AdminRestaurantLineIntegration = (): React.ReactElement => {
  const { t } = useTranslation();
  const params = useParams<{ id?: string }>();
  const [loader, setLoader] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showChannelSecret, setShowChannelSecret] = useState(false);
  const [showAccessToken, setShowAccessToken] = useState(false);
  const [settings, setSettings] = useState<LineIntegrationSettings>(INITIAL_SETTINGS);

  const restaurantId = params.id || null;

  const loadSettings = async (id: string): Promise<void> => {
    setLoader(true);

    try {
      const response = await getLineIntegrationSettings(id) as ServiceResponse<LineIntegrationSettings>;
      setSettings(response?.data || INITIAL_SETTINGS);
    } catch (error) {
      console.error(error);
    } finally {
      window.setTimeout(() => {
        setLoader(false);
      }, LOADER_TIMEOUT_TIME);
    }
  };

  useEffect(() => {
    if (!restaurantId) {
      setLoader(false);
      return;
    }
    void loadSettings(restaurantId);
  }, [restaurantId]);

  const formik = useFormik<LineIntegrationFormValues>({
    enableReinitialize: true,
    initialValues: {
      line_enabled: settings.line_enabled || false,
      line_login_channel_id: settings.line_login_channel_id || "",
      line_login_channel_secret: "",
      line_oa_basic_id: settings.line_oa_basic_id || "",
      line_channel_access_token: "",
    },
    validationSchema: Yup.object({
      line_login_channel_id: Yup.string().max(255, t("admin_line_invalid_channel_id")),
      line_login_channel_secret: Yup.string().max(2048, t("admin_line_invalid_channel_secret")),
      line_oa_basic_id: Yup.string()
        .matches(/^@?[A-Za-z0-9._-]*$/, t("admin_line_invalid_oa_basic_id"))
        .max(255, t("admin_line_invalid_oa_basic_id")),
      line_channel_access_token: Yup.string().max(4096, t("admin_line_invalid_channel_access_token")),
    }),
    onSubmit: async (values) => {
      if (!restaurantId) return;

      setSaving(true);

      const payload: UpdateLineIntegrationPayload = {
        o_id: restaurantId,
        line_enabled: values.line_enabled,
        line_login_channel_id: values.line_login_channel_id.trim(),
        line_oa_basic_id: values.line_oa_basic_id.trim(),
      };

      if (values.line_login_channel_secret.trim()) {
        payload.line_login_channel_secret = values.line_login_channel_secret.trim();
      }

      if (values.line_channel_access_token.trim()) {
        payload.line_channel_access_token = values.line_channel_access_token.trim();
      }

      try {
        const response = await updateLineIntegrationSettings(payload) as ServiceResponse<LineIntegrationSettings>;
        setSettings(response?.data || INITIAL_SETTINGS);
        formik.resetForm({
          values: {
            ...values,
            line_login_channel_secret: "",
            line_channel_access_token: "",
          },
        });
        void Swal.fire({
          icon: "success",
          text: response.message,
          ...SWAL_SETTINGS,
        } as any);
      } catch (error) {
        void Swal.fire({
          icon: "error",
          text: handleServerValidations((error as { response?: { data?: unknown } })?.response?.data || error),
          ...SWAL_SETTINGS,
        } as any);
      } finally {
        setSaving(false);
      }
    },
  });

  if (loader) {
    return (
      <div className="card custom-card">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h6 className="main-content-label mb-0">{t("admin_line_settings_title")}</h6>
            <Skeleton width={120} height={24} />
          </div>
          <Skeleton className="mb-3" height={140} />
          <Skeleton className="mb-2" height={48} count={6} />
        </div>
      </div>
    );
  }

  return (
    <div className="card custom-card">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h6 className="main-content-label mb-0">{t("admin_line_settings_title")}</h6>
        </div>

        <div
          className="d-flex align-items-center flex-wrap gap-2 mb-4 rounded px-3 py-3"
          role="alert"
          style={{ backgroundColor: "#f3f0ff", border: "1px solid #ddd6fe" }}
        >
          <span className="fw-semibold" style={{ color: "#4c51bf" }}>
            Need help setting up LINE? Open the onboarding guide.
          </span>
          <a
            href={NOTION_DOCS.LINE_SETUP}
            target="_blank"
            rel="noopener noreferrer"
            className="fw-semibold text-decoration-underline ms-1"
            style={{ color: "#6d5bd0" }}
          >
            View the LINE setup guide
          </a>
        </div>

        <form onSubmit={formik.handleSubmit}>
          <div className="form-check form-switch mb-4">
            <input
              className="form-check-input cp"
              type="checkbox"
              id="line_enabled"
              name="line_enabled"
              checked={formik.values.line_enabled}
              onChange={formik.handleChange}
            />
            <label className="form-check-label cp" htmlFor="line_enabled">
              {t("admin_line_enable_friend_gate")}
            </label>
          </div>

          <div className="form-group mb-4">
            <label className="form-label" htmlFor="line_login_channel_id">
              {t("admin_line_channel_id")}
            </label>
            <input
              id="line_login_channel_id"
              name="line_login_channel_id"
              type="text"
              className="form-control"
              value={formik.values.line_login_channel_id}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            <CustomError className="text-danger" name="line_login_channel_id" form={formik} />
          </div>

          <div className="form-group mb-4">
            <label className="form-label" htmlFor="line_login_channel_secret">
              {t("admin_line_channel_secret")}
            </label>
            <div className="input-group">
              <input
                id="line_login_channel_secret"
                name="line_login_channel_secret"
                type={showChannelSecret ? "text" : "password"}
                className="form-control border-end-0"
                value={formik.values.line_login_channel_secret}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={settings.line_login_channel_secret_masked || t("admin_line_secret_placeholder")}
              />
              <button
                type="button"
                className="input-group-text cp"
                onClick={() => setShowChannelSecret((current) => !current)}
              >
                <i className={`ti ${showChannelSecret ? "ti-eye-off" : "ti-eye"}`}></i>
              </button>
            </div>
            <CustomError className="text-danger" name="line_login_channel_secret" form={formik} />
          </div>

          <div className="form-group mb-4">
            <label className="form-label" htmlFor="line_oa_basic_id">
              {t("admin_line_oa_basic_id")}
            </label>
            <div className="input-group">
              <span className="input-group-text">@</span>
              <input
                id="line_oa_basic_id"
                name="line_oa_basic_id"
                type="text"
                className="form-control"
                value={formik.values.line_oa_basic_id.replace(/^@+/, "")}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => formik.setFieldValue("line_oa_basic_id", event.target.value.replace(/^@+/, ""))}
                onBlur={formik.handleBlur}
              />
            </div>
            <CustomError className="text-danger" name="line_oa_basic_id" form={formik} />
          </div>

          <div className="form-group mb-4">
            <label className="form-label" htmlFor="line_channel_access_token">
              {t("admin_line_access_token")}
            </label>
            <div className="input-group">
              <input
                id="line_channel_access_token"
                name="line_channel_access_token"
                type={showAccessToken ? "text" : "password"}
                className="form-control border-end-0"
                value={formik.values.line_channel_access_token}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={settings.line_channel_access_token_masked || undefined}
              />
              <button
                type="button"
                className="input-group-text cp"
                onClick={() => setShowAccessToken((current) => !current)}
              >
                <i className={`ti ${showAccessToken ? "ti-eye-off" : "ti-eye"}`}></i>
              </button>
            </div>
            <CustomError className="text-danger" name="line_channel_access_token" form={formik} />
          </div>

          <div className="row g-4">
            <div className="col-lg-7">
              <div className="border rounded p-3 h-100">
                <h6 className="mb-3">{t("admin_line_add_friend_url")}</h6>
                <div className="text-break">{settings.add_friend_url || t("admin_line_add_friend_url_hint")}</div>
              </div>
            </div>
            <div className="col-lg-5">
              <div className="border rounded p-3 h-100 d-flex flex-column align-items-center justify-content-center">
                <h6 className="mb-3">{t("admin_line_qr_preview")}</h6>
                {settings.add_friend_qr_value ? (
                  <QRCodeSVG value={settings.add_friend_qr_value} size={160} />
                ) : (
                  <span className="text-muted text-center">{t("admin_line_qr_preview_hint")}</span>
                )}
              </div>
            </div>
          </div>

          <div className="d-flex flex-wrap gap-3 mt-4 align-items-center">
            <button type="submit" className={`btn btn-primary${saving ? " btn-loader" : ""}`} disabled={saving}>
              {t("admin_line_update")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminRestaurantLineIntegration;
